import { useEffect, useState, useCallback } from "react";
import { useAppSelector } from "./redux";
import { adminRemindersApi } from "../repositories/adminReminders";
import { DueReminder } from "../types/reminder";
import Swal from "sweetalert2";

const SESSION_FLAG_KEY = "dueRemindersFetched";

const sessionFlagFor = (userId: number | string) => `${SESSION_FLAG_KEY}:${userId}`;

export function useDueReminders() {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [reminders, setReminders] = useState<DueReminder[]>([]);
  const [open, setOpen] = useState(false);

  const fetchDue = useCallback(async () => {
    if (!isAuthenticated || user?.role !== "admin" || !user?.id) return;

    try {
      const res = await adminRemindersApi.getDue();
      if (res?.status === 304 || res?.data == null) return;

      const payload = res.data;
      const due: DueReminder[] = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.data?.reminders)
          ? payload.data.reminders
          : Array.isArray(payload?.reminders)
            ? payload.reminders
            : [];

      if (due.length > 0) {
        setReminders(due);
        setOpen(true);
      } else {
        setReminders([]);
        setOpen(false);
      }
    } catch (err) {
      console.error("Failed to fetch due reminders:", err);
    }
  }, [isAuthenticated, user?.role, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== "admin" || !user?.id) return;

    const flag = sessionFlagFor(user.id);
    if (sessionStorage.getItem(flag) === "1") return;
    sessionStorage.setItem(flag, "1");

    fetchDue().catch(() => {
      sessionStorage.removeItem(sessionFlagFor(user.id));
    });
  }, [isAuthenticated, user?.role, user?.id, fetchDue]);

  const refetch = useCallback(() => {
    if (user?.id) {
      sessionStorage.removeItem(sessionFlagFor(user.id));
    }
    return fetchDue();
  }, [fetchDue, user?.id]);

  const dismiss = async (id: number) => {
    try {
      await adminRemindersApi.markViewed(id);
    } catch (err) {
      console.error("Failed to mark reminder as viewed:", err);
    }
    setReminders((prev) => {
      const next = prev.filter((r) => r.id !== id);
      if (next.length === 0) setOpen(false);
      return next;
    });
  };

  const deleteReminder = async (id: number) => {
    try {
      await adminRemindersApi.remove(id);
    } catch (err) {
      console.error("Failed to delete reminder:", err);
      Swal.fire({ title: "Error", text: "Failed to delete reminder.", icon: "error" });
      return;
    }
    await refetch();
  };

  const close = () => setOpen(false);

  return { reminders, open, close, dismiss, deleteReminder, refetch };
}
